import React from 'react';
import { X, Bot } from 'lucide-react';
import { renderStatusBadge } from '../../../utils/statusBadge';
import CitationBlock from '../../common/components/chat/CitationBlock';
import ConsultationFooter from '../../common/components/chat/ConsultationFooter';

/**
 * 질의 내역 상세 보기 모달 컴포넌트.
 * @param {object} inquiry - 선택된 질의 데이터
 * @param {function} onClose - 모달 닫기 콜백
 */
const InquiryDetailModal = ({ inquiry, onClose }) => {
    if (!inquiry) return null;

    const isError = inquiry.answer_status === 'error' || inquiry.answer_status === 'timeout';

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-2 sm:p-4 bg-gray-900/40 backdrop-blur-sm animate-fade-in">
            <div className="bg-white w-full max-w-2xl rounded-2xl sm:rounded-[32px] shadow-2xl overflow-hidden flex flex-col max-h-[90vh] sm:max-h-[85vh]">
                {/* 헤더 */}
                <div className="px-4 sm:px-6 py-3 sm:py-4 border-b flex justify-between items-center bg-gray-50/50">
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-0.5 sm:mb-1 flex-wrap">
                            <h3 className="font-bold text-gray-900 text-sm sm:text-base">질의 내역 상세</h3>
                            <div className="scale-90 sm:scale-100 origin-left">
                                {renderStatusBadge(inquiry.answer_status)}
                            </div>
                        </div>
                        <p className="text-[10px] sm:text-xs text-gray-400 truncate">{inquiry.user_name} · {inquiry.created_at}</p>
                    </div>
                    <button onClick={onClose} className="p-1.5 sm:p-2 hover:bg-gray-200 rounded-full transition text-gray-500 flex-shrink-0">
                        <X size={18} className="sm:size-5" />
                    </button>
                </div>

                {/* 본문 */}
                <div className="p-4 sm:p-6 overflow-y-auto space-y-4 sm:space-y-6">
                    {/* 질문 */}
                    <div className="flex gap-3 sm:gap-4 items-start">
                        <div className="w-[36px] h-[36px] min-w-[36px] min-h-[36px] sm:w-[40px] sm:h-[40px] sm:min-w-[40px] sm:min-h-[40px] bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-black text-sm sm:text-base flex-none aspect-square">Q</div>
                        <div className="pt-1.5 sm:pt-2 font-bold text-gray-800 leading-relaxed text-sm sm:text-base">{inquiry.question}</div>
                    </div>

                    {/* 답변 */}
                    <div className="flex gap-3 sm:gap-4 items-start pb-2">
                        <div className={`w-[36px] h-[36px] min-w-[36px] min-h-[36px] sm:w-[40px] sm:h-[40px] sm:min-w-[40px] sm:min-h-[40px] rounded-full flex items-center justify-center text-white font-bold flex-none aspect-square ${isError ? 'bg-red-500 shadow-lg shadow-red-100' : 'bg-gray-800 shadow-lg shadow-gray-200'}`}>
                            <Bot size={18} className="sm:size-5" />
                        </div>
                        <div className="pt-1 w-full min-w-0">
                            <div className={`text-[13px] sm:text-[15px] leading-relaxed whitespace-pre-wrap p-4 sm:p-6 rounded-2xl ${isError ? 'bg-red-50 text-red-600 border border-red-100' : 'bg-[#f8fafc] text-gray-600 border border-gray-100 shadow-sm'}`}>
                                <div dangerouslySetInnerHTML={{ __html: inquiry.answer }} />
                                {inquiry.meta && (
                                    <div className="mt-4 pt-4 border-t border-gray-200/50">
                                        <CitationBlock
                                            lawRefs={inquiry.meta.law_refs}
                                            citations={inquiry.meta.citations}
                                        />
                                        <ConsultationFooter />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InquiryDetailModal;
